import { FC, useState, useEffect, useCallback} from 'react';
import { observer } from 'mobx-react';
import { debounce, some, startCase, toLower } from 'lodash';
import { sourceService } from 'services/sourceService';
import SourceStore from 'stores/source';
import { useTranslation } from 'react-i18next';
import ownerStore from 'stores/owner';
import { Link } from 'react-router-dom';
import { validate } from 'services/ownersService';
import workspaceStore from 'stores/workspace';
import classNames from 'classnames';
import { Form, Button, Input, Select, Row, Col, message } from 'antd';
import { LANGUAGES, REGEXP_EMAIL } from "constants/mixed"
import { CloseOutlined, PlusOutlined, HomeOutlined, UserOutlined, SolutionOutlined } from "@ant-design/icons";

import { keys } from 'lodash';

import { IOwner } from "declarations/contact";
import { ISource } from "declarations/source";

interface IData {
  owner?: IOwner,
  onCreateOwner?(params: IOwner): void;
  onCancelButton(): void;
}

interface IOwnerInfo {
  id?: null | number,
  value?: string,
  _destroy?: boolean
}

const { Option, OptGroup } = Select;
const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 12 },
};
const formItemRow = {
  wrapperCol: { sm: {span: 18, offset: 6} }
};
const InitialState = {
  owner: {
    id: null,
    owner_type: '0',
    name: '',
    website: '',
    address: '',
    owner_person: '',
    nie: '',
    source_id: undefined,
    languages: [],
    other_languages: [],
    phones_attributes: [{ value: '', phonable_type: "Owner" }],
    emails_attributes: [{ value: '', letterable_type: "Owner" }]
  }
}
const OwnerForm:  FC<IData> = observer(({owner, onCancelButton, onCreateOwner}) => {

  const [state, setState] = useState<any>({...InitialState});
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [errors, setErrors] = useState<{[key: string]: any}>({});


  useEffect(() => {
    setState({...InitialState})
    if (owner) {
      form.setFieldsValue({ ...owner, owner_type: String(owner.owner_type) })
      setState({
        ...state,
        owner: {
          ...state.owner,
          ...owner
        }
      })
    }
    sourceService.loadResources()
    return () => {
      form.resetFields()
      setState(InitialState)
    }
  }, [])

  useEffect(() => {
    if (owner) {
      form.setFieldsValue({ ...owner, owner_type: String(owner.owner_type) })
      if (owner.emails_attributes) {
        let errorsValue = {}
        owner.emails_attributes.map(({value}) =>
          validate(value, workspaceStore.selectedId as number, owner?.id).catch(({response}: any) => {
            const respData = response.data;
            errorsValue = { ...errorsValue, [value]: respData }
            setErrors({ ...errors, ...errorsValue})
          })
        )
      };
      setState({
        ...state,
        owner: {
          ...state.owner,
          ...owner
        }
      })
    }
  }, [state.owner?.id])

  useEffect(() => {
    form.resetFields();
    setErrors({});
  }, [workspaceStore.selectedId])

  const handleSubmit = async () => {
    ownerStore.clearErrors();
    await form.validateFields();
    if (!some(form.getFieldsError(), ({ errors }) => errors.length)) {
      const values = form.getFieldsValue();
      const ownerValue: IOwner = { ...state.owner, ...values };
      ownerValue['emails_attributes'] = ownerValue['emails_attributes'].filter((item: { value: string }) => item.value)
      ownerValue['phones_attributes'] = ownerValue['phones_attributes'].filter((item: { value: string }) => item.value)
      if (state.owner.id) {
        await ownerStore.update(state.owner.id, ownerValue)
        if(!ownerStore.saveError) {
          message.success({ content: 'Owner updated!' })
          onCancelButton();
        } else {
          message.error({ content: ownerStore.saveError.response.data.message })
        }
      }

      if (onCreateOwner && !state.owner.id) {
        onCreateOwner(ownerValue)
      }
    }
  }

  const removeOwnerInfo = (attributesTitle: string, elIndex: number) => {
    const owner: any = state.owner;
    const itemObject = owner[attributesTitle][elIndex];

    if (itemObject.id) {
      const newItem = { ...itemObject, _destroy: 'true' }
      const attributesArray = owner[attributesTitle].map((item: IOwnerInfo) => item.id == itemObject.id ? newItem : item)

      setState({
        ...state,
        owner: {
          ...state.owner,
          [attributesTitle]: attributesArray
        }
      })
    } else {
      const newItems = owner[attributesTitle].filter((item: IOwnerInfo, index: number) => index != elIndex);
      setState({
        ...state,
        owner: {
          ...state.owner,
          [attributesTitle]: newItems
        }
      })
    }
  }

  const addOwnerInfo = (type: string) => {
    const owner: any = state.owner;
    const attributesTitle = `${type}s_attributes`;
    setState({
      ...state,
      owner: {
        ...state.owner,
        [attributesTitle]: [...owner[attributesTitle], ...[{ id: null, [`${type === "email" ? "email" : "phon"}able_type`]: "Owner",  value: '' }]]
      }
    })
  }

  const debouncedValidate = useCallback(
    debounce((email, allErrors) => {
      validate(email, workspaceStore.selectedId as number, owner?.id).catch(({response}: any) => {
        const respData = response.data;
        setErrors({ ...allErrors, [email]: respData })
      })
    }, 500), []
  );

  const handleInfoChange = (e: any, field: string, index: number) => {
    const owner: any = state.owner;
    const value = e.target.value;
    const newValue = { ...owner[field][index], value: value }
    setState({
      ...state,
      owner: {
        ...state.owner,
        [field]: owner[field].map((item: IOwnerInfo, indexItem: number) => index == indexItem ? newValue : item)
      }
    })
    setErrors({ ...errors, [value]: undefined })
    if (value.match(REGEXP_EMAIL)) {
      debouncedValidate(value, errors)
    }
  }

  const getErrors = (email?: string) => {
    const errorsData = email && errors[email];
    if (errorsData){
      return <div>
        {errorsData.map((item: any) =>
        <div>
          <span>{t(`owners.new.${item.type}Exists`)} <Link target="_blank" to={`/${item.type}s/${item.resource_id}/overview`}>{t("owners.new.viewDetails")}  ({workspaceStore.getWorkspaceName(item.workspace_id) || t("anotherWorkspace")})</Link></span>
        </div>)}
      </div>
    }
    return undefined;
  }

  const onTypeChange = (value: string) => {
    setState({
      ...state,
      owner: {
        ...state.owner,
        owner_type: value
      }
    })
  }

  const renderOwnerInfo = (type: string) => {
    const owner: any = state.owner;
    const attributesTitle = `${type}s_attributes`;
    const lableTitle = startCase(toLower(type));

    return owner[attributesTitle].map((item: IOwnerInfo, index: number) => {
      if (item._destroy) {
        return;
      }
      return (
        <Form.Item
          {...formItemLayout}
          label={index == 0 ? (lableTitle + " :") : ' '}
          className={classNames({ 'more-owner-items': index != 0 })}
          colon={false}
          status="error"
          help={<span className="mb-20 text-orange">{getErrors(item.value)}</span>}
        >
          <Row align='middle' className='fx-nw'>
            <Input value={item.value} placeholder={lableTitle} type={type} onChange={(e) => handleInfoChange(e, attributesTitle, index)} />
            <CloseOutlined className='ml-10' onClick={() => removeOwnerInfo(attributesTitle, index)} />
          </Row>
        </Form.Item>
      )
    })
  }

  const renderSources = () => {
    const sources = SourceStore.groupedValues;
    const sourceKeys = keys(sources)

    return sourceKeys.map((category: string) => {
      return <OptGroup key={`source_${category}`} label={category}>
        {sources[category].map((source: ISource) => <Option value={source.id} key={`source_${source.id}`}>{source.name}</Option>)}
      </OptGroup>
    })
  }

  console.log("state", state.owner)

  return (
    <div>
      <div className="property-steps-container">
        <Form form={form}>
          <Form.Item
            {...formItemLayout}
            label={t('owners.filter.type')}
            name="owner_type"
            initialValue="0"
          >
            <Select onChange={onTypeChange} filterOption={(input: any, option: any) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
              <Option value="0"><UserOutlined className='mr-5' />{t('owners.new.type.person')}</Option>
              <Option value="1"><HomeOutlined className='mr-5' />{t('owners.new.type.company')}</Option>
              <Option value="2"><SolutionOutlined className='mr-5' />{t('owners.new.type.agency')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            {...formItemLayout}
            label={t('owners.new.name')}
            name="name"
            rules={[{ required: true, message: t('validates.cantBeEmpty') }]}
          >
            <Input placeholder={t('owners.new.name')} />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {({getFieldValue}) => {
              const isNotPerson = getFieldValue("owner_type") !== "0";
              return isNotPerson &&
                <Form.Item
                  {...formItemLayout}
                  label={t('owners.new.website')}
                  name="website"
                >
                  <Input placeholder={t('owners.new.website')} />
                </Form.Item>
            }}
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {({getFieldValue}) => {
              const isNotPerson = getFieldValue("owner_type") !== "0";
              return isNotPerson &&
                <Form.Item
                  {...formItemLayout}
                  label={t('owners.new.address')}
                  name="address"
                >
                  <Input placeholder={t('owners.new.address')} value={state.owner.address} />
                </Form.Item>
            }}
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {({getFieldValue}) => {
              const isNotPerson = getFieldValue("owner_type") !== "0";
              return isNotPerson &&
                <Form.Item
                  {...formItemLayout}
                  label={t('owners.new.ownerPerson')}
                  name="owner_person"
                >
                  <Input placeholder={t('owners.new.ownerPerson')} value={state.owner.owner_person} />
                </Form.Item>
            }}
          </Form.Item>
          {renderOwnerInfo('email')}
          <Form.Item
            {...formItemRow}
          >
            <a className='add-item-link' onClick={() => addOwnerInfo('email')}>
              <PlusOutlined className='mr-5' />
              {t('owners.new.email')}
            </a>
          </Form.Item>
          {renderOwnerInfo('phone')}
          <Form.Item
            {...formItemRow}
          >
            <a className='add-item-link' onClick={() => addOwnerInfo('phone')}>
              <PlusOutlined className='mr-5' />
              {t('owners.new.phone')}
            </a>
          </Form.Item>
          <Form.Item
            {...formItemLayout}
            name="languages"
            label={t('owners.new.mainLang')}
            initialValue={[]}
          >
            <Select mode="multiple" filterOption={(input: any, option: any) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
              {LANGUAGES.map((lng: string) => (
                <Option key={`languages-${lng}`} value={lng}>{t(`contacts.new.lng.${lng}`)}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            {...formItemLayout}
            label={t('contacts.new.otherLang')}
            name="other_languages"
          >
            <Select mode="multiple" filterOption={(input: any, option: any) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
              {LANGUAGES.map((lng: string) => (
                  <Option key={`other-languages-${lng}`} value={lng}>{t(`contacts.new.lng.${lng}`)}</Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            {...formItemLayout}
            label={t('contacts.new.nie')}
            name="nie"
          >
            <Input placeholder={t('contacts.new.nie')} />
          </Form.Item>
          <Form.Item
            {...formItemLayout}
            label={t('contacts.new.source')}
            name="source_id"
            rules={[{ required: true, message: t('validates.cantBeEmpty') }]}
          >
            <Select
              placeholder={t('contacts.new.placeholderSource')}
              filterOption={(input: any, option: any) => typeof(option.props.children) == 'string' && option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              showSearch
            >
              {renderSources()}
            </Select>
          </Form.Item>
        </Form>
        <Row>
          <Col sm={{ offset: 6 }}>
            <Button type='primary' className='mr-15' onClick={handleSubmit} >{t('save')}</Button>
            <Button onClick={onCancelButton}>{t('cancel')}</Button>
          </Col>
        </Row>
      </div>
    </div>
  );
});

export default OwnerForm;
